import { useState, useCallback, useRef, useEffect } from 'react';
import { useList, useDelete, useUpdate, useGo } from '@refinedev/core';
import { Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { UniversalFilters } from './UniversalFilters';
import { BulkActionsBar } from './BulkActionsBar';
import { RowActionsMenu } from './RowActionsMenu';
import { SoftDeleteDialog } from './SoftDeleteDialog';
import type {
  BaseEntity,
  EntityConfig,
  FilterValues,
  ColumnType,
} from './types';
import { UI_STRINGS } from './locale';

const PAGE_SIZE = 20;

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function formatDate(value: Date | string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : dateFormatter.format(d);
}

/**
 * Правильная русская плюрализация
 * @param n Число
 * @param one Форма для 1 (например: "год")
 * @param few Форма для 2-4 (например: "года")
 * @param many Форма для 5+ (например: "лет")
 * @returns Строка с правильным числом и формой
 */
function pluralize(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) {
    return `${n} ${many}`;
  }
  if (last > 1 && last < 5) {
    return `${n} ${few}`;
  }
  if (last === 1) {
    return `${n} ${one}`;
  }
  return `${n} ${many}`;
}

function formatCurrency(value: number | string | null | undefined, currency = 'UZS'): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (num === null || num === undefined || !Number.isFinite(num)) {
    return '—';
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: currency === 'UZS' ? 0 : 2,
  }).format(num);
}

function renderByType(type: ColumnType, value: BaseEntity[keyof BaseEntity], record?: BaseEntity): React.ReactNode {
  switch (type) {
    case 'date': {
      return formatDate(value as string | null);
    }
    case 'currency': {
      const recordCurrency = record && 'currency' in record
        ? String((record as Record<string, unknown>).currency)
        : undefined;
      return formatCurrency(value as number | null, recordCurrency);
    }
    case 'boolean': {
      return Boolean(value) ? (
        <Badge variant="default">{UI_STRINGS.yes}</Badge>
      ) : (
        <Badge variant="secondary">{UI_STRINGS.no}</Badge>
      );
    }
    case 'duration': {
      const days = Number(value);
      if (!Number.isFinite(days) || days <= 0) {
        return '—';
      }
      if (days % 365 === 0) {
        const years = days / 365;
        return pluralize(years, 'год', 'года', 'лет');
      }
      if (days % 30 === 0) {
        const months = days / 30;
        return pluralize(months, 'месяц', 'месяца', 'месяцев');
      }
      if (days % 7 === 0) {
        const weeks = days / 7;
        return pluralize(weeks, 'неделя', 'недели', 'недель');
      }
      return pluralize(days, 'день', 'дня', 'дней');
    }
    case 'progress': {
      const pct = Number(value);
      return Number.isFinite(pct) ? `${pct}%` : '—';
    }
    default: {
      return String(value);
    }
  }
}

interface DeleteState<TRecord extends BaseEntity> {
  record: TRecord;
  mode: 'soft' | 'permanent';
}

interface UniversalTableProps<TRecord extends BaseEntity> {
  config: EntityConfig<TRecord>;
}

interface RefineFilter {
  field: string;
  operator: 'eq' | 'contains';
  value: string | boolean | number;
}

/**
 * Универсальная таблица сущностей.
 * Оборачивает DataTable и подключает:
 *  - поиск с дебаунсом
 *  - UniversalFilters
 *  - BulkActionsBar
 *  - RowActionsMenu
 *  - soft-delete / restore / permanent-delete
 *  - переключатель "Показать удалённые"
 */
export function UniversalTable<TRecord extends BaseEntity>({
  config,
}: UniversalTableProps<TRecord>) {
  const go = useGo();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState<TRecord> | null>(null);
  const [selectedRows, setSelectedRows] = useState<TRecord[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 350);
  }, []);

  // Build Refine filters
  const refineFilters: RefineFilter[] = [];

  if (search) {
    refineFilters.push({ field: 'search', operator: 'contains', value: search });
  }

  if (showDeleted && config.softDelete) {
    refineFilters.push({ field: 'withDeleted', operator: 'eq', value: true });
  }

  for (const [key, val] of Object.entries(filterValues)) {
    if (val === undefined || val === null || val === '') {
      continue;
    }
    if (typeof val === 'object') {
      const range = val as { from?: string; to?: string };
      if (range.from) {
        refineFilters.push({ field: `${key}From`, operator: 'eq', value: range.from });
      }
      if (range.to) {
        refineFilters.push({ field: `${key}To`, operator: 'eq', value: range.to });
      }
    } else {
      refineFilters.push({ field: key, operator: 'eq', value: val });
    }
  }

  const { result, query } = useList<TRecord>({
    resource: config.resource,
    pagination: { currentPage: page, pageSize: PAGE_SIZE, mode: 'server' },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters: refineFilters,
  });

  const data = result.data;
  const total = result.total ?? 0;
  const isLoading = query.isLoading;
  const isError = query.isError;

  const { mutate: softDelete, mutation: softDeleteMutation } = useDelete();
  const { mutate: restore } = useUpdate<TRecord>();

  const isSoftDeleting = softDeleteMutation.isPending;

  const handleEdit = useCallback(
    (record: TRecord) => {
      go({ to: `/${config.resource}/edit/${record.id}` });
    },
    [go, config.resource],
  );

  const handleSoftDelete = useCallback((record: TRecord) => {
    setDeleteState({ record, mode: 'soft' });
  }, []);

  const handleRestore = useCallback(
    (record: TRecord) => {
      restore({
        resource: config.resource,
        id: record.id,
        values: { restore: true },
      });
    },
    [restore, config.resource],
  );

  const handlePermanentDelete = useCallback((record: TRecord) => {
    setDeleteState({ record, mode: 'permanent' });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteState) {
      return;
    }
    softDelete(
      {
        resource: config.resource,
        id: deleteState.record.id,
        meta: deleteState.mode === 'permanent' ? { permanent: true } : undefined,
      },
      {
        onSuccess: () => {
          setDeleteState(null);
          void query.refetch();
        },
        onError: () => {
          setDeleteState(null);
        },
      },
    );
  }, [deleteState, softDelete, config.resource, query]);

  // Build DataTable columns from config
  const columns: Column<TRecord>[] = config.columns.map((col) => {
    const colRender = col.render;
    const colType = col.type;

    let renderFn;
    if (colRender) {
      renderFn = (value: TRecord[keyof TRecord], record: TRecord) =>
        colRender(value, record);
    } else if (colType) {
      renderFn = (value: TRecord[keyof TRecord], record: TRecord) =>
        renderByType(colType, value as BaseEntity[keyof BaseEntity], record);
    }

    return {
      key: col.key as keyof TRecord,
      header: col.label,
      sortable: col.sortable,
      width: col.width,
      render: renderFn,
    };
  });

  // Actions column
  const actionsColumn: Column<TRecord> = {
    key: '__actions__' as keyof TRecord,
    header: '',
    width: '56px',
    render: (_value, record) => (
      <RowActionsMenu
        record={record}
        config={config}
        onEdit={config.canEdit ? handleEdit : undefined}
        onSoftDelete={config.softDelete ? handleSoftDelete : undefined}
        onRestore={config.softDelete ? handleRestore : undefined}
        onPermanentDelete={config.softDelete ? handlePermanentDelete : undefined}
      />
    ),
  };

  const allColumns = [...columns, actionsColumn];

  // Build bulk actions
  const bulkActionsForDataTable: BulkAction<TRecord>[] = config.bulkActions.map((action) => ({
    label: action.label,
    icon: action.icon,
    variant: action.variant,
    onClick: (rows) => {
      void action.onClick(rows);
    },
    showOnlyWhenSelected: true,
  }));

  let entityName: string | undefined;
  if (deleteState) {
    entityName = config.nameField
      ? String((deleteState.record as Record<string, unknown>)[config.nameField])
      : String(deleteState.record.id);
  }

  const activeFilterCount = Object.values(filterValues).filter(
    (v) => v !== undefined && v !== null && v !== '',
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <FormHeader
        title={config.label.plural}
        sticky
        actions={
          config.canCreate ? (
            <Button onClick={() => go({ to: `/${config.resource}/create` })}>
              <Plus className="mr-2 h-4 w-4" />
              {UI_STRINGS.create(config.label.singular.toLowerCase())}
            </Button>
          ) : undefined
        }
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={UI_STRINGS.search(config.label.plural.toLowerCase())}
          value={searchInput}
          onChange={(e) => handleSearchInput(e.target.value)}
          className="h-9 w-64"
        />

        {config.filters.length > 0 && (
          <UniversalFilters
            fields={config.filters}
            values={filterValues}
            onChange={(vals) => {
              setFilterValues(vals);
              setPage(1);
            }}
            onReset={() => {
              setFilterValues({});
              setPage(1);
            }}
            label={UI_STRINGS.filters(activeFilterCount)}
          />
        )}

        {config.softDelete && (
          <Button
            variant={showDeleted ? 'secondary' : 'outline'}
            size="sm"
            className="gap-2"
            onClick={() => {
              setShowDeleted((prev) => !prev);
              setPage(1);
            }}
          >
            {showDeleted ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            {showDeleted ? UI_STRINGS.hideDeleted : UI_STRINGS.showDeleted}
          </Button>
        )}
      </div>

      {/* Error state */}
      {isError && (
        <div className="rounded-lg border border-destructive bg-destructive/5 p-4">
          <p className="text-sm font-medium text-destructive">
            {UI_STRINGS.error}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {query.error instanceof Error ? query.error.message : UI_STRINGS.errorUnknown}
          </p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <DataTable
          columns={allColumns}
          data={data}
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          isLoading={isLoading}
          onPageChange={(p) => {
            setPage(p);
            setSelectedRows([]);
          }}
          onRowClick={(record) => {
            go({ to: `/${config.resource}/show/${record.id}` });
          }}
          emptyMessage={UI_STRINGS.noData(config.label.plural.toLowerCase())}
          selectable={config.bulkActions.length > 0}
          rowKey="id"
          sort={sort}
          onSortChange={setSort}
          bulkActions={bulkActionsForDataTable}
          onSelectionChange={setSelectedRows}
        />
      )}

      {/* Floating bulk actions bar */}
      {selectedRows.length > 0 && config.bulkActions.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedRows.length}
          selectedRows={selectedRows}
          actions={config.bulkActions}
          onClearSelection={() => setSelectedRows([])}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteState && (
        <SoftDeleteDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setDeleteState(null);
            }
          }}
          mode={deleteState.mode}
          entityLabel={config.label.singular}
          entityName={deleteState.mode === 'permanent' ? entityName : undefined}
          onConfirm={handleConfirmDelete}
          isPending={isSoftDeleting}
        />
      )}
    </div>
  );
}
